import qs from "qs";
import { useCallback, useEffect, useState } from "react";

import { usePromise } from "@touchtech/web-hooks";

import { ALL_STYLES_PAGE_SIZE, LocalStorageKeys } from "@is/constants";
import { useCollections } from "@is/hooks/useCollections";
import { LocalStorageService } from "@is/services";
import {
  api,
  createAllFavoriteUrl,
  createCollectionByIdUrl,
  createCollectionDemoUrl,
  createCollectionsReorderUrl,
  createDemoCollectionByStoreUrl,
  createNewCollection,
} from "@is/services/api";
import {
  CollectionListItem,
  CollectionStylesResponse,
  CollectionsResponse,
} from "@is/types";
import { Collection } from "@is/types/collections";

type Props = {
  filterOutOfStockItems?: boolean;
};

async function handleAddCollection(collection: Partial<CollectionListItem>) {
  const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);
  const url = createNewCollection();

  const data = await api.post(url, { ...collection, storeId });

  return data;
}

async function handleDeleteCollection(collectionId: string) {
  const url = createCollectionByIdUrl(collectionId);

  const data = await api.delete(url);

  return data;
}

async function handleFetchAllView(
  page: number,
  pageSize: number,
  filterOutOfStockItems: boolean
) {
  const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);
  const url = createAllFavoriteUrl(storeId);

  const queryString = qs.stringify({
    page,
    pageSize,
    filterOutOfStockItems,
  });

  const data = await api.get(`${url}?${queryString}`);

  return data;
}

async function handleFetchDemoCollection() {
  const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);
  const url = createDemoCollectionByStoreUrl(storeId);

  const data = await api.get(url);

  return data;
}

async function handleUpdateCollectionDemo(
  collectionId: string,
  isDemoCollection: boolean
) {
  const url = createCollectionDemoUrl(collectionId);

  const data = await api.patch(url, { isDemoCollection });

  return data;
}

async function handleReorderCollections(orderedCollectionIds: string[]) {
  const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);

  if (!storeId) {
    throw new Error("Cannot reorder collections: storeId is missing");
  }

  const url = createCollectionsReorderUrl(storeId);

  const data = await api.put(url, { orderedCollectionIds });

  return data;
}

export const useCollectionsList = ({
  filterOutOfStockItems = false,
}: Props) => {
  const {
    collections: data,
    isLoading,
    error,
    revalidateCollections,
  } = useCollections({ filterOutOfStockItems });
  const [collections, setCollections] = useState<CollectionsResponse>(null);

  const [{ isPending }, dispatch] = usePromise();

  useEffect(() => {
    setCollections(data);
  }, [data]);

  const addCollection = useCallback(
    async (collection: Partial<CollectionListItem>) => {
      const newCollection = (await dispatch(
        handleAddCollection(collection)
      )) as Collection;

      setCollections((collections) => ({
        ...collections,
        count: collections.count + 1,
        totalCount: collections.totalCount + 1,
        items: [
          ...collections.items,
          {
            id: newCollection.id,
            name: newCollection.name,
            thumbnails: [],
            totalItems: 0,
          },
        ],
      }));

      return newCollection;
    },
    [dispatch]
  );

  const deleteCollection = useCallback(
    async (collectionId: string) => {
      await dispatch(handleDeleteCollection(collectionId));

      setCollections((collections) => ({
        ...collections,
        count: collections.count - 1,
        totalCount: collections.totalCount - 1,
        items: collections.items.filter((item) => item.id !== collectionId),
      }));
    },
    [dispatch]
  );

  const getAllCollectionItems = useCallback(
    async (
      page = 1,
      pageSize = ALL_STYLES_PAGE_SIZE,
      filterOutOfStockItems = false
    ) => {
      const allViewData = (await dispatch(
        handleFetchAllView(page, pageSize, filterOutOfStockItems)
      )) as CollectionStylesResponse;

      return allViewData;
    },
    [dispatch]
  );

  const getDemoCollection = useCallback(async () => {
    const demoCollection = (await dispatch(
      handleFetchDemoCollection()
    )) as Collection;

    return demoCollection;
  }, [dispatch]);

  const updateCollectionDemo = useCallback(
    async (collectionId: string, isDemoCollection: boolean) => {
      const updatedCollection = (await dispatch(
        handleUpdateCollectionDemo(collectionId, isDemoCollection)
      )) as Collection;

      // Update the local collections state to reflect the demo status change
      setCollections((collections) => ({
        ...collections,
        items: collections.items.map((item) =>
          item.id === collectionId ? { ...item, isDemoCollection } : item
        ),
      }));

      return updatedCollection;
    },
    [dispatch]
  );

  const reorderCollections = useCallback(
    async (orderedIds: string[]) => {
      // Optimistic update.
      //
      // Only store-owned rows move. Brand-wide rows hold the index the server placed them at —
      // their position is set in the admin dashboard and is locked here — so we refill just the
      // store-owned slots in order and leave every other slot untouched. Appending anything not
      // in orderedIds (as this used to do) would yank an interleaved brand row to the end.
      setCollections((prev) => {
        if (!prev) return prev;

        const allItemsCollection = prev.items.find((item) => !item.id);
        const listed = prev.items.filter((item) => !!item.id);

        const reordered = orderedIds
          .map((id) => listed.find((item) => item.id === id))
          .filter(Boolean) as CollectionListItem[];

        // Anything store-owned but absent from orderedIds (e.g. concurrently added) keeps its
        // relative place at the end of the movable set rather than jumping the whole list.
        const unseen = listed.filter(
          (item) => !item.isBrandWide && !orderedIds.includes(item.id)
        );
        const movable = [...reordered, ...unseen];

        let next = 0;
        const merged = listed.map((item) =>
          item.isBrandWide ? item : movable[next++] ?? item
        );

        return {
          ...prev,
          items: allItemsCollection ? [allItemsCollection, ...merged] : merged,
        };
      });

      try {
        await dispatch(handleReorderCollections(orderedIds));
        // Re-sync even on success: the server owns the merged order, including where the
        // brand-wide rows land.
        revalidateCollections();
      } catch {
        revalidateCollections();
      }
    },
    [dispatch, revalidateCollections]
  );

  return {
    collections,
    isLoading,
    isPending,
    error,
    addCollection,
    deleteCollection,
    getAllCollectionItems,
    getDemoCollection,
    updateCollectionDemo,
    revalidateCollections,
    reorderCollections,
  };
};
